import React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Tooltip } from '@mui/material';
import { IconPlugConnected } from '@tabler/icons-react';

import type { UserPermissionGroupDto } from '@shared/services/apiService';

interface GroupChipProps {
  group: UserPermissionGroupDto;
  /** The group grants nothing the user doesn't already have. */
  redundant?: boolean;
}

/** A group the user is in. SSO memberships carry an icon; the tooltip says what it means. */
export const GroupChip: React.FC<GroupChipProps> = ({ group, redundant = false }) => {
  const { t } = useTranslation();
  const fromDirectory = group.source === 'directory';
  const builtIn = Boolean(group.builtIn);
  const hints = [
    builtIn && t(`accessControl.users.dialog.roles.${group.builtIn}.description`),
    fromDirectory && t('accessControl.users.directoryTooltip'),
    redundant && t('accessControl.users.redundantTooltip'),
  ].filter(Boolean);

  return (
    <Tooltip title={hints.join(' ')}>
      <Chip
        size="small"
        // Outlined marks the built-in group, which every user has exactly one of.
        variant={builtIn || redundant ? 'outlined' : 'filled'}
        color={fromDirectory ? 'info' : builtIn ? 'primary' : 'default'}
        icon={fromDirectory ? <IconPlugConnected size={14} /> : undefined}
        label={group.name}
        sx={redundant ? { opacity: 0.6 } : undefined}
      />
    </Tooltip>
  );
};
